import { useEffect, useRef, useState } from 'react'
import { CameraOff } from 'lucide-react'

/*
 * De camera als QR-lezer. BarcodeDetector waar de browser hem heeft
 * (Chrome op Android), anders jsQR (iPhone, iPad). Het beeld blijft in de
 * browser; er gaat niets naar een server behalve de code die eruit komt.
 */

interface Detector { detect(b: CanvasImageSource): Promise<{ rawValue: string }[]> }
type JsQR = (d: Uint8ClampedArray, w: number, h: number, o?: { inversionAttempts?: string }) => { data: string } | null

export function Scanner({ gevonden, voorkant = false, kaal = false, opFout }: {
  gevonden: (tekst: string) => void; voorkant?: boolean; kaal?: boolean; opFout?: (reden: string) => void
}) {
  const video = useRef<HTMLVideoElement>(null)
  const [fout, zetFoutStaat] = useState<string | null>(null)
  const klaar = useRef(false)
  const terug = useRef(gevonden)
  terug.current = gevonden
  /* Wie een eigen terugval heeft (de tablet), hoort het ook. */
  const meld = useRef(opFout)
  meld.current = opFout
  const zetFout = (t: string) => { zetFoutStaat(t); meld.current?.(t) }

  useEffect(() => {
    let stroom: MediaStream | null = null
    let stop = false
    let tik: number | undefined
    klaar.current = false

    ;(async () => {
      if (!navigator.mediaDevices?.getUserMedia) { zetFout('Deze browser kan de camera niet gebruiken. Typ de code.'); return }
      try {
        stroom = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: voorkant ? 'user' : { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        })
      } catch {
        zetFout('Geen toegang tot de camera. Sta de camera toe, of typ de code.')
        return
      }
      if (stop) { stroom.getTracks().forEach((t) => t.stop()); return }
      const v = video.current
      if (!v) return
      v.srcObject = stroom
      v.setAttribute('playsinline', 'true')
      await v.play().catch(() => {})

      const BD = (window as unknown as { BarcodeDetector?: new (o: { formats: string[] }) => Detector }).BarcodeDetector
      let detector: Detector | null = null
      if (BD) { try { detector = new BD({ formats: ['qr_code'] }) } catch { detector = null } }
      const jsqr: JsQR | null = detector ? null : ((await import('jsqr')).default as unknown as JsQR)
      const doek = document.createElement('canvas')
      const ctx = doek.getContext('2d', { willReadFrequently: true })

      const kijk = async () => {
        if (stop || klaar.current) return
        try {
          if (v.readyState >= 2) {
            let tekst: string | null = null
            if (detector) {
              const r = await detector.detect(v)
              tekst = r[0]?.rawValue ?? null
            } else if (jsqr && ctx) {
              const w = 480
              const h = Math.round((v.videoHeight / v.videoWidth) * w) || 360
              doek.width = w; doek.height = h
              ctx.drawImage(v, 0, 0, w, h)
              tekst = jsqr(ctx.getImageData(0, 0, w, h).data, w, h, { inversionAttempts: 'attemptBoth' })?.data ?? null
            }
            if (tekst && !klaar.current) {
              klaar.current = true
              navigator.vibrate?.(60)
              terug.current(tekst)
              return
            }
          }
        } catch { /* volgend beeld */ }
        tik = window.setTimeout(kijk, 140)
      }
      kijk()
    })()

    return () => {
      stop = true
      if (tik) clearTimeout(tik)
      stroom?.getTracks().forEach((t) => t.stop())
    }
  }, [voorkant])

  if (fout) {
    return <div className="melding let"><CameraOff size={20} /><span>{fout}</span></div>
  }
  return (
    <div className="camera">
      <video ref={video} muted playsInline autoPlay />
      {!kaal && <div className="kader" />}
    </div>
  )
}
